import { useMutation } from "@apollo/client/react";
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  DeleteOutlined,
  DragOutlined,
  FolderOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { App, Badge, Button, Flex, Popover, Space, Table, Tag, Tooltip, Typography } from "antd";
import { type KeyboardEvent, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useEnterToNext, useFocusMarker } from "../../../hooks/useEnterToNext.ts";
import {
  AddLabelIngredientDocument,
  type AllergenType,
  type IngredientFragment,
  LabelIngredientAction,
  LabelIngredientPlacement,
  MoveLabelIngredientDocument,
  RemoveLabelIngredientDocument,
  UpdateLabelIngredientDocument,
} from "../../../gql/generated/graphqlSchema.tsx";
import { AllergenSelect } from "../../../components/AllergenSelect.tsx";
import { AutoSaveInput } from "../../../components/AutoSaveInput.tsx";
import { LocaleNumberInput } from "../../../components/LocaleNumberInput.tsx";
import { useSaving } from "../../../hooks/useSaving.tsx";
import { useIngredient } from "../IngredientContext.tsx";
import { labelOrderColumn } from "../labelIngredient.styles.ts";
import { type FlatLabelRow, flattenLabelRows, LABEL_INDENT_PX } from "../labelIngredientUtils.ts";
import { css } from "@linaria/core";
import { tableScroll, tableSticky } from "../../../layout/constants.ts";
import { ComparePhotosButton } from "../ComparePhotosButton.tsx";

const percentageInput = css`
  width: 100px;
`;
const moveGrid = css`
  display: grid;
  grid-template-areas:
    ".    up   ."
    "left .    right"
    ".    down .";
  gap: var(--ant-margin-xxs);
  width: fit-content;
`;
const moveUp = css`
  grid-area: up;
`;
const moveDown = css`
  grid-area: down;
`;
const moveLeft = css`
  grid-area: left;
`;
const moveRight = css`
  grid-area: right;
`;

type LabelItem = IngredientFragment["labelIngredients"][number];

export const IngredientLabelIngredientsTab = () => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { ingredient, setIngredient } = useIngredient();
  const { trackSaving } = useSaving();
  const [addLabelIngredient] = useMutation(AddLabelIngredientDocument);
  const [updateLabelIngredient] = useMutation(UpdateLabelIngredientDocument);
  const [removeLabelIngredient] = useMutation(RemoveLabelIngredientDocument);
  const [moveLabelIngredient] = useMutation(MoveLabelIngredientDocument);

  const items = ingredient.labelIngredients;
  const rows = useMemo(() => flattenLabelRows(items), [items]);

  const tableRef = useRef<HTMLDivElement>(null);
  const { focusAtDeferred: tryFocusTimeout } = useFocusMarker(tableRef);

  const handleAddRoot = useCallback(() => {
    trackSaving(() =>
      addLabelIngredient({
        variables: {
          ingredientId: ingredient.id,
          input: { name: "", parentId: null },
        },
      })
        .then((r) => setIngredient(r.data?.addLabelIngredient.refreshedIngredient))
        .then(tryFocusTimeout(rows.length)),
    );
  }, [trackSaving, addLabelIngredient, ingredient.id, tryFocusTimeout, rows.length, setIngredient]);

  const handleAddChild = useCallback(
    (parent: LabelItem, index: number) => {
      trackSaving(() =>
        addLabelIngredient({
          variables: {
            ingredientId: ingredient.id,
            input: { name: "", parentId: parent.id },
          },
        })
          .then((r) => setIngredient(r.data?.addLabelIngredient.refreshedIngredient))
          .then(tryFocusTimeout(index + 1)),
      );
    },
    [trackSaving, addLabelIngredient, ingredient.id, tryFocusTimeout, setIngredient],
  );

  const handleAddAfter = useCallback(
    (target: LabelItem, index: number) => {
      trackSaving(() =>
        addLabelIngredient({
          variables: {
            ingredientId: ingredient.id,
            input: {
              name: "",
              parentId: target.parentId,
              targetId: target.id,
              placement: LabelIngredientPlacement.After,
            },
          },
        })
          .then((r) => setIngredient(r.data?.addLabelIngredient.refreshedIngredient))
          .then(tryFocusTimeout(index + 1)),
      );
    },
    [trackSaving, addLabelIngredient, ingredient.id, tryFocusTimeout, setIngredient],
  );

  const handleEditName = useCallback(
    (item: LabelItem, name: string) => {
      trackSaving(() =>
        updateLabelIngredient({
          variables: {
            ingredientId: ingredient.id,
            labelIngredientId: item.id,
            input: { name },
          },
        }).then((r) => setIngredient(r.data?.updateLabelIngredient.refreshedIngredient)),
      );
    },
    [ingredient.id, updateLabelIngredient, trackSaving, setIngredient],
  );

  const handleEditPercentage = useCallback(
    (item: LabelItem, value: number | null) => {
      trackSaving(() =>
        updateLabelIngredient({
          variables: {
            ingredientId: ingredient.id,
            labelIngredientId: item.id,
            input: { percentage: value },
          },
        }).then((r) => setIngredient(r.data?.updateLabelIngredient.refreshedIngredient)),
      );
    },
    [ingredient.id, updateLabelIngredient, trackSaving, setIngredient],
  );

  const handleEditAllergens = useCallback(
    (item: LabelItem, allergens: AllergenType[]) => {
      trackSaving(() =>
        updateLabelIngredient({
          variables: {
            ingredientId: ingredient.id,
            labelIngredientId: item.id,
            input: { allergens },
          },
        }).then((r) => setIngredient(r.data?.updateLabelIngredient.refreshedIngredient)),
      );
    },
    [ingredient.id, updateLabelIngredient, trackSaving, setIngredient],
  );

  const handleDelete = useCallback(
    (item: LabelItem) => {
      modal.confirm({
        title: t("ingredients.labelIngredients.deleteTitle"),
        content: t("ingredients.labelIngredients.deleteConfirm"),
        okButtonProps: { danger: true, icon: <DeleteOutlined /> },
        cancelButtonProps: { icon: <CloseOutlined /> },
        okText: t("ingredients.labelIngredients.delete"),
        onOk: () =>
          trackSaving(() =>
            removeLabelIngredient({
              variables: { ingredientId: ingredient.id, labelIngredientId: item.id },
            }).then((r) => setIngredient(r.data?.removeLabelIngredient.refreshedIngredient)),
          ),
      });
    },
    [ingredient.id, removeLabelIngredient, trackSaving, setIngredient, modal, t],
  );

  const handleMove = useCallback(
    (item: LabelItem, action: LabelIngredientAction) => {
      trackSaving(() =>
        moveLabelIngredient({
          variables: { ingredientId: ingredient.id, labelIngredientId: item.id, input: { action } },
        }).then((r) => setIngredient(r.data?.moveLabelIngredient.refreshedIngredient)),
      );
    },
    [ingredient.id, moveLabelIngredient, trackSaving, setIngredient],
  );

  const focusNext = useEnterToNext(tableRef);

  const handleEnter = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!focusNext()) {
        // Last row — add a new sibling after the last item
        const lastRow = rows[rows.length - 1];
        if (lastRow) {
          // Blur to trigger saving of the current input before adding the new row
          if (e.target instanceof HTMLInputElement) {
            e.target.blur();
          }
          handleAddAfter(lastRow.item, rows.length - 1);
        }
      }
    },
    [focusNext, handleAddAfter, rows],
  );

  return (
    <Flex gap="middle" vertical>
      <Space wrap>
        {items.length > 0 ? (
          <Button color="primary" icon={<PlusOutlined />} onClick={handleAddRoot} variant="solid">
            {t("ingredients.labelIngredients.add")}
          </Button>
        ) : null}
        <ComparePhotosButton />
      </Space>

      <div ref={tableRef}>
        <Table<FlatLabelRow>
          bordered
          columns={[
            {
              title: t("ingredients.labelIngredients.nameColumn"),
              key: "name",
              render: (_, row) => (
                <Flex align="center" gap="small">
                  <Tag
                    className={labelOrderColumn}
                    color="volcano"
                    style={{ marginLeft: row.depth * LABEL_INDENT_PX }}
                    variant="solid"
                  >
                    <strong>{row.order}</strong>
                  </Tag>
                  <AutoSaveInput
                    classNames={{ input: "marker" }}
                    defaultValue={row.item.name}
                    onCommit={(v) => handleEditName(row.item, v)}
                    onPressEnter={handleEnter}
                    placeholder={t("ingredients.labelIngredients.namePlaceholder")}
                    showCaseToggle
                  />
                </Flex>
              ),
            },
            {
              title: "%",
              key: "percentage",
              width: 120,
              render: (_, row) => (
                <LocaleNumberInput
                  className={percentageInput}
                  defaultValue={row.item.percentage ?? undefined}
                  onCommit={(v) => handleEditPercentage(row.item, v)}
                  onPressEnter={handleEnter}
                  step={1}
                  suffix="%"
                />
              ),
            },
            {
              title: t("allergens.title"),
              key: "allergens",
              width: 60,
              align: "center",
              fixed: "right",
              render: (_, row) => (
                <AllergenSelect
                  onChange={(allergens) => handleEditAllergens(row.item, allergens)}
                  value={row.item.allergens}
                >
                  <Badge color="cyan" count={row.item.allergens.length} size="small">
                    <Button
                      color={row.item.allergens.length > 0 ? "cyan" : undefined}
                      icon={<WarningOutlined />}
                      variant="outlined"
                    />
                  </Badge>
                </AllergenSelect>
              ),
            },
            {
              fixed: "right",
              title: t("ingredients.actions"),
              key: "actions",
              width: 100,
              render: (_, row, index) => (
                <Space>
                  <Tooltip destroyOnHidden title={t("ingredients.labelIngredients.addAfter")}>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => handleAddAfter(row.item, index)}
                      variant="outlined"
                    />
                  </Tooltip>
                  <Tooltip destroyOnHidden title={t("ingredients.labelIngredients.addChild")}>
                    <Button
                      icon={<FolderOutlined />}
                      onClick={() => handleAddChild(row.item, index)}
                      variant="outlined"
                    />
                  </Tooltip>
                  <Popover
                    content={
                      <div className={moveGrid}>
                        <Button
                          className={moveUp}
                          icon={<ArrowUpOutlined />}
                          onClick={() => handleMove(row.item, LabelIngredientAction.MoveUp)}
                          // size="small"
                        />
                        <Button
                          className={moveLeft}
                          icon={<ArrowLeftOutlined />}
                          onClick={() => handleMove(row.item, LabelIngredientAction.IndentOut)}
                          // size="small"
                        />
                        <Button
                          className={moveRight}
                          icon={<ArrowRightOutlined />}
                          onClick={() => handleMove(row.item, LabelIngredientAction.IndentIn)}
                          // size="small"
                        />
                        <Button
                          className={moveDown}
                          icon={<ArrowDownOutlined />}
                          onClick={() => handleMove(row.item, LabelIngredientAction.MoveDown)}
                          // size="small"
                        />
                      </div>
                    }
                    destroyOnHidden
                  >
                    <Button icon={<DragOutlined />} variant="outlined" />
                  </Popover>
                  <Tooltip destroyOnHidden title={t("ingredients.labelIngredients.delete")}>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(row.item)}
                      variant="outlined"
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
          dataSource={rows}
          locale={{
            emptyText: (
              <Flex align="center" gap="middle" vertical>
                <Typography.Text type="secondary">{t("ingredients.labelIngredients.noIngredients")}</Typography.Text>
                <Button color="primary" icon={<PlusOutlined />} onClick={handleAddRoot} variant="solid">
                  {t("ingredients.labelIngredients.add")}
                </Button>
              </Flex>
            ),
          }}
          pagination={false}
          rowKey={(row) => row.item.id}
          scroll={tableScroll}
          size="small"
          sticky={tableSticky}
        />
      </div>
    </Flex>
  );
};
